import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import ExclamationCircleFilled from '@ant-design/icons/ExclamationCircleFilled';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import PaperClipOutlined from '@ant-design/icons/PaperClipOutlined';
import { useTranslation } from 'react-i18next';
import type { InquiryFileStatus } from '@/types/chat/chat';
import { STATE_KEYS, type UploadItem } from './attachmentState';
import { EXCLUSION_KEYS, formatMb } from './quota';
import styles from './composer.module.css';

/**
 * The Q&A's attachments in the composer (spec §3): files on their way
 * (validating, uploading with a bar, cancelled, failed, unsupported,
 * reattachment required) and the ones the server has, with their
 * processing state. Each row can be removed; busy uploads can be
 * cancelled, failed ones retried.
 */

interface AttachmentListProps {
  uploads: UploadItem[];
  attachments: InquiryFileStatus[];
  /** An attachment being removed on the server. */
  removingId: string | null;
  canRetry: (item: UploadItem) => boolean;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onRemoveUpload: (id: string) => void;
  onReattach: (id: string) => void;
  onRemoveAttachment: (fileId: string) => void;
  /** Chat only: attachments stay but are not sent. */
  chatOnly?: boolean;
}

/**
 * Why a failed attachment cannot be used, in the UI language: the server's
 * localized explanation, else the reason in the dictionary (image-only
 * PDF, no text), else a general one.
 */
const useUnusableText = () => {
  const { t } = useTranslation();
  return (file: InquiryFileStatus) => {
    if (file.unusable_message) return file.unusable_message;
    const key = file.unusable_reason
      ? EXCLUSION_KEYS[file.unusable_reason]
      : undefined;
    return key
      ? t('v2.common.labelValue', {
          label: t('v2.attachments.attUnusable'),
          value: t(key),
        })
      : t('v2.attachments.attUnusable');
  };
};

const serverState = (file: InquiryFileStatus) =>
  file.status === 'finished_processing'
    ? 'ready'
    : file.status === 'failed'
      ? 'unusable'
      : 'processing';

const AttachmentList = ({
  uploads,
  attachments,
  removingId,
  canRetry,
  onCancel,
  onRetry,
  onRemoveUpload,
  onReattach,
  onRemoveAttachment,
  chatOnly = false,
}: AttachmentListProps) => {
  const { t } = useTranslation();
  const unusableText = useUnusableText();
  const known = new Set(attachments.map(file => String(file.id)));
  // A finished upload is shown by the server's row once it is listed.
  const local = uploads.filter(
    item => !(item.state === 'complete' && known.has(String(item.fileId))),
  );
  if (local.length === 0 && attachments.length === 0) return null;

  return (
    <ul
      className={styles.attachments}
      aria-label={t('v2.attachments.listLabel')}
      data-testid="attachment-list"
    >
      {local.map(item => {
        const busy = item.state === 'validating' || item.state === 'uploading';
        const bad =
          item.state === 'failed' ||
          item.state === 'unsupported' ||
          item.state === 'reattach';
        const status = item.message
          ? item.message
          : item.reasonKey
            ? t(item.reasonKey, item.reasonValues)
            : t(STATE_KEYS[item.state], { p: item.percent });
        return (
          <li
            key={item.id}
            className={styles.attachment}
            data-state={item.state}
            data-unused={chatOnly || undefined}
            data-testid="attachment-upload"
          >
            {busy ? (
              <LoadingOutlined aria-hidden className={styles.attIcon} />
            ) : bad ? (
              <ExclamationCircleFilled
                aria-hidden
                className={styles.attIconBad}
              />
            ) : item.state === 'complete' ? (
              <CheckCircleFilled aria-hidden className={styles.attIconOk} />
            ) : (
              <PaperClipOutlined aria-hidden className={styles.attIcon} />
            )}
            <span className={styles.attBody}>
              <span className={styles.attName}>{item.name}</span>
              <span className={styles.attStatus} role="status">
                {formatMb(item.size)} MB ·{' '}
                {item.state === 'uploading' && !item.message
                  ? t('v2.attachments.att_uploading', { p: item.percent })
                  : status}
                {chatOnly
                  ? t('v2.common.paren', { text: t('v2.composer.notUsedChat') })
                  : null}
              </span>
              {item.state === 'uploading' ? (
                <span
                  className={styles.attBar}
                  role="progressbar"
                  aria-label={item.name}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={item.percent}
                >
                  <span style={{ width: `${item.percent}%` }} />
                </span>
              ) : null}
            </span>
            {busy ? (
              <button
                type="button"
                className={styles.textButton}
                onClick={() => onCancel(item.id)}
                data-testid="attachment-cancel"
              >
                {t('v2.attachments.cancelAtt')}
              </button>
            ) : null}
            {(item.state === 'failed' || item.state === 'cancelled') &&
            canRetry(item) ? (
              <button
                type="button"
                className={styles.textButton}
                onClick={() => onRetry(item.id)}
                data-testid="attachment-retry"
              >
                {t('v2.attachments.retryAtt')}
              </button>
            ) : null}
            {item.state === 'reattach' ? (
              <button
                type="button"
                className={styles.textButton}
                onClick={() => onReattach(item.id)}
                data-testid="attachment-reattach"
              >
                {t('v2.attachments.attachAgain')}
              </button>
            ) : null}
            <button
              type="button"
              className={styles.iconButton}
              aria-label={t('v2.common.removeNamed', { name: item.name })}
              title={t('v2.attachments.removeAtt')}
              onClick={() => {
                // A finished upload is an attachment on the server: remove
                // it there too, not just the row.
                if (item.state === 'complete' && item.fileId != null) {
                  onRemoveAttachment(String(item.fileId));
                }
                onRemoveUpload(item.id);
              }}
              data-testid="attachment-remove"
            >
              <CloseOutlined aria-hidden />
            </button>
          </li>
        );
      })}
      {attachments.map(file => {
        const state = serverState(file);
        return (
          <li
            key={`server-${file.id}`}
            className={styles.attachment}
            data-state={state}
            data-unused={chatOnly || undefined}
            data-testid="attachment-server"
          >
            {state === 'processing' ? (
              <LoadingOutlined aria-hidden className={styles.attIcon} />
            ) : state === 'unusable' ? (
              <ExclamationCircleFilled
                aria-hidden
                className={styles.attIconBad}
              />
            ) : (
              <CheckCircleFilled aria-hidden className={styles.attIconOk} />
            )}
            <span className={styles.attBody}>
              <span className={styles.attName}>{file.name}</span>
              <span className={styles.attStatus} role="status">
                {state === 'ready'
                  ? t('v2.attachments.attReadyServer')
                  : state === 'unusable'
                    ? unusableText(file)
                    : t('v2.attachments.attProcessing')}
                {chatOnly
                  ? t('v2.common.paren', { text: t('v2.composer.notUsedChat') })
                  : null}
              </span>
            </span>
            {removingId === String(file.id) ? (
              <LoadingOutlined aria-hidden className={styles.attIcon} />
            ) : (
              <button
                type="button"
                className={styles.iconButton}
                aria-label={t('v2.common.removeNamed', { name: file.name })}
                title={t('v2.attachments.removeAtt')}
                onClick={() => onRemoveAttachment(String(file.id))}
                data-testid="attachment-server-remove"
              >
                <CloseOutlined aria-hidden />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default AttachmentList;
